import type { Event as SentryEvent } from "@sentry/nextjs";

export function isReactVirtuosoStackOverflow(event: SentryEvent): boolean {
  if (
    !event.exception?.values?.some(
      (ex) =>
        ex.type === "RangeError" &&
        ex.value?.includes("Maximum call stack size exceeded"),
    )
  ) {
    return false;
  }

  const args = event.extra?.arguments;
  if (Array.isArray(args)) {
    const hasScrollTrigger = args.some(
      (a) =>
        typeof a === "object" && a !== null && "type" in a && a.type === "scroll",
    );
    if (hasScrollTrigger) {
      const allFrames =
        event.exception?.values?.flatMap(
          (ex) => ex.stacktrace?.frames ?? [],
        ) ?? [];
      const hasBundledFrame = allFrames.some((f) =>
        f.filename?.includes("_next/static/chunks/"),
      );
      if (hasBundledFrame) {
        return true;
      }
    }
  }

  const hasVirtuosoFrames = event.exception!.values!.some((ex) => {
    const frames = ex.stacktrace?.frames;
    if (!frames || frames.length === 0) return false;
    const framesWithFilename = frames.filter((f) => f.filename);
    if (framesWithFilename.length === 0) return false;
    const virtuosoCount = framesWithFilename.filter(
      (f) =>
        f.filename!.includes("react-virtuoso") ||
        f.filename!.includes("react_virtuoso"),
    ).length;
    return virtuosoCount / framesWithFilename.length >= 0.8;
  });

  if (hasVirtuosoFrames) return true;

  const values = event.exception!.values!;
  const hasInAppFrame = values.some((ex) =>
    ex.stacktrace?.frames?.some((frame) => frame.in_app === true),
  );
  if (hasInAppFrame) return false;

  const allFrames = values.flatMap((ex) => ex.stacktrace?.frames ?? []);
  const hasNamedFrame = allFrames.some(
    (frame) =>
      (typeof frame.filename === "string" && frame.filename.length > 0) ||
      (typeof frame.function === "string" && frame.function.length > 0) ||
      typeof frame.lineno === "number",
  );
  return !hasNamedFrame;
}

export function isIosWebViewBridgeNoise(event: SentryEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;

  const hasAnyInAppFrame = values.some((ex) =>
    ex.stacktrace?.frames?.some((f) => f.in_app === true),
  );
  if (hasAnyInAppFrame) return false;

  const WK_BRIDGE_STRINGS =
    /WKWebView was deallocated|WKWebView API client did not respond/i;

  return values.some(
    (ex) =>
      ex.mechanism?.type?.includes("onunhandledrejection") === true &&
      typeof ex.value === "string" &&
      WK_BRIDGE_STRINGS.test(ex.value),
  );
}

export function isNodeWebstreamsAbortRace(event: SentryEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;

  const hasAnyInAppFrame = values.some((ex) =>
    ex.stacktrace?.frames?.some((f) => f.in_app === true),
  );
  if (hasAnyInAppFrame) return false;

  return values.some((ex) => {
    if (ex.type !== "TypeError") return false;
    if (typeof ex.value !== "string") return false;
    if (!/transformAlgorithm is not a function/i.test(ex.value)) return false;

    const frames = ex.stacktrace?.frames ?? [];
    const framesWithFilename = frames.filter((f) => typeof f.filename === "string");
    if (framesWithFilename.length === 0) return false;
    const allInternal = framesWithFilename.every((f) =>
      f.filename!.startsWith("node:internal/"),
    );
    const hasWebstreamsFrame = framesWithFilename.some((f) =>
      f.filename!.startsWith("node:internal/webstreams/"),
    );
    return allInternal && hasWebstreamsFrame;
  });
}

export function isExtensionMessagingNoise(event: SentryEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;

  const hasAnyInAppFrame = values.some((ex) =>
    ex.stacktrace?.frames?.some((f) => f.in_app === true),
  );
  if (hasAnyInAppFrame) return false;

  const EXTENSION_RUNTIME_STRINGS = /runtime\.sendMessage/i;

  return values.some(
    (ex) =>
      ex.mechanism?.type?.includes("onunhandledrejection") === true &&
      typeof ex.value === "string" &&
      EXTENSION_RUNTIME_STRINGS.test(ex.value),
  );
}

export function isSupabaseAuthLockStolen(event: SentryEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;

  return values.some(
    (ex) =>
      typeof ex.value === "string" &&
      /Lock was stolen by another request/i.test(ex.value),
  );
}

export function isRouterStateHeaderParseError(event: SentryEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;

  const hasAnyInAppFrame = values.some((ex) =>
    ex.stacktrace?.frames?.some((f) => f.in_app === true),
  );
  if (hasAnyInAppFrame) return false;

  return values.some(
    (ex) =>
      typeof ex.value === "string" &&
      /router state header was sent but could not be parsed/i.test(ex.value),
  );
}
